import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Loader2, Trash2, Banknote } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useCompany } from "@/context/CompanyContext";
import { PageHeader } from "@/components/PageHeader";
import { EmptyState } from "@/components/EmptyState";
import { StatusPill } from "@/components/StatusPill";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { getErrorMessage } from "@/lib/errors";

const CAN_MANAGE = ["ORG_ADMIN", "ACCOUNTS"];
const EMPTY_ALLOCATION = { ra_bill_id: "", amount: "" };

export default function ClientPayments() {
  const { user } = useAuth();
  const canManage = CAN_MANAGE.includes(user?.role);
  const { companies } = useCompany() || {};
  const [payments, setPayments] = useState([]);
  const [clients, setClients] = useState([]);
  const [raBills, setRaBills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [payRes, clientRes, billRes] = await Promise.all([
        api.get("/revenue/client-payments"), api.get("/revenue/clients"), api.get("/revenue/ra-bills"),
      ]);
      setPayments(payRes.data); setClients(clientRes.data); setRaBills(billRes.data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const clientName = (id) => clients.find((c) => c.id === id)?.name || "—";

  const post = async (payment) => {
    setBusyId(payment.id);
    try {
      await api.post(`/revenue/client-payments/${payment.id}/post`);
      toast.success("Payment posted to GL & allocated to RA bills");
      load();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="space-y-6" data-testid="client-payments-page">
      <PageHeader title="Client Payments" description="Receive a client payment and allocate it against one or more outstanding RA bills." action={canManage && <CreatePaymentDialog companies={companies} clients={clients} raBills={raBills} onCreated={load} />} />
      <div className="rounded-xl border border-border bg-card">
        {!loading && payments.length === 0 ? (
          <EmptyState title="No client payments yet" testId="client-payments-empty-state" />
        ) : (
          <Table>
            <TableHeader><TableRow><TableHead>Number</TableHead><TableHead>Client</TableHead><TableHead>Date</TableHead><TableHead className="text-right">Amount</TableHead><TableHead>Status</TableHead><TableHead></TableHead></TableRow></TableHeader>
            <TableBody>
              {payments.map((p) => (
                <TableRow key={p.id} data-testid={`client-payment-row-${p.number}`}>
                  <TableCell className="font-mono text-xs text-foreground">{p.number}</TableCell>
                  <TableCell className="text-foreground">{clientName(p.client_id)}</TableCell>
                  <TableCell className="text-muted-foreground">{p.receipt_date}</TableCell>
                  <TableCell className="text-right font-mono text-xs font-semibold text-foreground">{p.amount}</TableCell>
                  <TableCell><StatusPill value={p.status} /></TableCell>
                  <TableCell className="text-right">
                    {canManage && p.status === "DRAFT" && (
                      <Button size="sm" variant="outline" disabled={busyId === p.id} onClick={() => post(p)} data-testid={`post-client-payment-button-${p.number}`}>
                        <Banknote className="h-3.5 w-3.5" /> Post
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
}

function CreatePaymentDialog({ companies, clients, raBills, onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ company_id: "", client_id: "", receipt_date: "", amount: "", remarks: "" });
  const [allocations, setAllocations] = useState([{ ...EMPTY_ALLOCATION }]);
  const [saving, setSaving] = useState(false);
  const set = (field) => (e) => setForm({ ...form, [field]: e.target.value });
  const updateAlloc = (idx, field, value) => setAllocations(allocations.map((a, i) => (i === idx ? { ...a, [field]: value } : a)));

  const clientBills = raBills.filter((b) => b.client_id === form.client_id && b.status === "POSTED");

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        allocations: allocations.filter((a) => a.ra_bill_id && a.amount).map((a) => ({ ra_bill_id: a.ra_bill_id, amount: a.amount })),
      };
      await api.post("/revenue/client-payments", payload);
      toast.success("Client payment created");
      setForm({ company_id: "", client_id: "", receipt_date: "", amount: "", remarks: "" });
      setAllocations([{ ...EMPTY_ALLOCATION }]);
      setOpen(false);
      onCreated?.();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button data-testid="create-client-payment-button"><Plus className="h-4 w-4" /> New Payment</Button></DialogTrigger>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader><DialogTitle>New Client Payment</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3.5" data-testid="create-client-payment-form">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Company</Label>
              <Select required value={form.company_id} onValueChange={(v) => setForm({ ...form, company_id: v })}>
                <SelectTrigger data-testid="client-payment-company-select" className="mt-1.5"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{(companies || []).map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Client</Label>
              <Select required value={form.client_id} onValueChange={(v) => setForm({ ...form, client_id: v })}>
                <SelectTrigger data-testid="client-payment-client-select" className="mt-1.5"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{(clients || []).map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Date</Label><Input required type="date" data-testid="client-payment-date-input" value={form.receipt_date} onChange={set("receipt_date")} className="mt-1.5" /></div>
            <div><Label>Amount</Label><Input required type="number" step="0.01" data-testid="client-payment-amount-input" value={form.amount} onChange={set("amount")} className="mt-1.5" /></div>
          </div>
          <div>
            <Label>Allocate to RA bills (optional)</Label>
            <div className="mt-1.5 space-y-2">
              {allocations.map((a, idx) => (
                <div key={idx} className="flex items-center gap-2" data-testid={`client-payment-allocation-row-${idx}`}>
                  <Select value={a.ra_bill_id} onValueChange={(v) => updateAlloc(idx, "ra_bill_id", v)}>
                    <SelectTrigger className="flex-1" data-testid={`client-payment-allocation-bill-select-${idx}`}><SelectValue placeholder="Select an RA bill" /></SelectTrigger>
                    <SelectContent>{clientBills.map((b) => <SelectItem key={b.id} value={b.id}>{b.number} (net {b.net_receivable})</SelectItem>)}</SelectContent>
                  </Select>
                  <Input type="number" step="0.01" placeholder="Amount" className="w-28" value={a.amount} onChange={(e) => updateAlloc(idx, "amount", e.target.value)} data-testid={`client-payment-allocation-amount-input-${idx}`} />
                  {allocations.length > 1 && <Button type="button" size="sm" variant="ghost" onClick={() => setAllocations(allocations.filter((_, i) => i !== idx))}><Trash2 className="h-3.5 w-3.5 text-rose-400" /></Button>}
                </div>
              ))}
            </div>
            <Button type="button" size="sm" variant="outline" className="mt-2" onClick={() => setAllocations([...allocations, { ...EMPTY_ALLOCATION }])} data-testid="add-client-payment-allocation-button"><Plus className="h-3.5 w-3.5" /> Add allocation</Button>
          </div>
          <DialogFooter>
            <Button type="submit" disabled={saving} data-testid="create-client-payment-submit-button">{saving && <Loader2 className="h-4 w-4 animate-spin" />} Create</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
